import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Badge } from '@/components/ui/Badge';
import { 
  Database, 
  Calendar, 
  RefreshCw, 
  CheckCircle, 
  AlertCircle, 
  XCircle
} from 'lucide-react';
import { tituloService } from '@/services/tituloService';
import { ExternalApiConfig, ExternalImportResult } from '@/types';
import { toast } from 'react-hot-toast';

interface ExternalImportCardProps {
  onImportComplete?: () => void;
  onAutoDistribute?: () => void;
}

export const ExternalImportCard: React.FC<ExternalImportCardProps> = ({ onImportComplete, onAutoDistribute }) => {
  const [config, setConfig] = useState<ExternalApiConfig | null>(null);
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState<ExternalImportResult | null>(null);
  // Função para calcular período padrão (01/06/2025 até hoje + 20 dias)
  const getDefaultPeriod = () => {
    const hoje = new Date();
    
    // Data inicial fixa: 01/06/2025
    const dataInicio = new Date('2025-06-01');
    
    // Data final sempre é hoje + 20 dias
    const dataFim = new Date(hoje);
    dataFim.setDate(hoje.getDate() + 20); // Hoje + 20 dias
    dataFim.setHours(0, 0, 0, 0); // Zerar horas para garantir consistência
    
    return {
      data_inicio: dataInicio.toISOString().split('T')[0]!,
      data_fim: dataFim.toISOString().split('T')[0]!,
    };
  };

  const [period, setPeriod] = useState(getDefaultPeriod());

  // Atualizar data final sempre que o componente for renderizado ou quando necessário
  useEffect(() => {
    const hoje = new Date();
    const dataFim = new Date(hoje);
    dataFim.setDate(hoje.getDate() + 20); // Hoje + 20 dias
    dataFim.setHours(0, 0, 0, 0);
    const dataFimStr = dataFim.toISOString().split('T')[0]!;
    
    // Sempre garantir que a data final é hoje + 20 dias
    if (period.data_fim !== dataFimStr) {
      setPeriod(prev => ({
        ...prev,
        data_fim: dataFimStr,
      }));
    }
  }, [period.data_fim]);

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    try {
      setLoading(true);
      const configData = await tituloService.getExternalApiConfig();
      setConfig(configData);
      
      // Sempre usar período padrão (01/06/2025 até hoje + 20 dias)
      setPeriod(getDefaultPeriod());
    } catch (error: any) {
      toast.error('Erro ao carregar configuração da API externa');
    } finally {
      setLoading(false);
    }
  };

  const handleImport = async () => {
    if (!config?.enabled) {
      toast.error('Integração com sistema financeiro externo não está configurada');
      return;
    }

    try {
      setImporting(true);
      setImportResult(null);

      // Garantir que a data final é sempre hoje + 20 dias antes de enviar
      const hoje = new Date();
      const dataFim = new Date(hoje);
      dataFim.setDate(hoje.getDate() + 20); // Hoje + 20 dias
      dataFim.setHours(0, 0, 0, 0);
      const periodToSend = {
        ...period,
        data_fim: dataFim.toISOString().split('T')[0]!,
      };
      
      const result = await tituloService.importFromExternalApi(periodToSend);
      setImportResult(result);

      if (result.success) {
        toast.success(`Importação concluída! ${result.totalProcessed} títulos processados.`);
        onImportComplete?.();
        
        // Disparar evento para atualizar o indicador de última integração no Header
        window.dispatchEvent(new CustomEvent('importCompleted'));
        
        // Aguardar 5 segundos e executar distribuição automática
        if (onAutoDistribute) {
          toast('A distribuição de títulos será iniciada em 5 segundos...', { 
            icon: 'ℹ️',
            duration: 5000 
          });
          setTimeout(() => {
            onAutoDistribute();
          }, 5000);
        }
      } else {
        toast.error('Erro na importação. Verifique os detalhes.');
      }
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Erro ao importar do sistema financeiro externo');
    } finally {
      setImporting(false);
    }
  };

  const getConfigStatus = () => {
    if (!config) return { status: 'loading', message: 'Carregando...', color: 'bg-gray-500' };
    
    if (!config.enabled) {
      return { status: 'disabled', message: 'Integração desabilitada', color: 'bg-gray-500' };
    }
    
    if (!config.hasUrl || !config.hasToken || !config.hasCnpj) {
      return { status: 'incomplete', message: 'Configuração incompleta', color: 'bg-yellow-500' };
    }
    
    return { status: 'ready', message: 'Pronto para uso', color: 'bg-green-500' };
  };

  const configStatus = getConfigStatus();

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center">
          <Database className="h-5 w-5 mr-2" />
          Sincronizar com Sistema Financeiro
          <Badge className={`ml-2 ${configStatus.color} text-white`}>
            {configStatus.message}
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-gray-600 dark:text-slate-300">
          Sincronize títulos diretamente do sistema financeiro externo sem necessidade de arquivo CSV.
        </p>

        {!config?.enabled && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex items-center space-x-3">
              <AlertCircle className="h-5 w-5 text-yellow-600" />
              <div>
                <p className="text-sm font-medium text-yellow-800">
                  Integração não configurada
                </p>
                <p className="text-xs text-yellow-600">
                  Configure as variáveis de ambiente para habilitar a integração
                </p>
              </div>
            </div>
          </div>
        )}

        {config?.enabled && (
          <>
            {/* Seleção de Período */}
            <div className="space-y-3">
              <div className="flex items-center space-x-2">
                <Calendar className="h-4 w-4 text-gray-500" />
                <span className="text-sm font-medium">Período de Consulta</span>
              </div>
              
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-gray-600 dark:text-slate-400 mb-1">
                    Data Início
                  </label>
                  <Input
                    type="date"
                    value={period.data_inicio}
                    onChange={(e) => setPeriod(prev => ({ ...prev, data_inicio: e.target.value }))}
                    disabled={importing}
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 dark:text-slate-400 mb-1">
                    Data Fim <span className="text-blue-600 dark:text-blue-400">(Hoje + 20 dias)</span>
                  </label>
                  <Input
                    type="date"
                    value={period.data_fim}
                    disabled={true}
                    className="bg-gray-100 dark:bg-slate-700 cursor-not-allowed"
                    title="A data final é sempre hoje + 20 dias e não pode ser alterada"
                  />
                  <p className="text-xs text-gray-500 dark:text-slate-400 mt-1">
                    A data final é automaticamente definida como hoje + 20 dias
                  </p>
                </div>
              </div>
            </div>

            {/* Status da Importação */}
            {importing && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-center space-x-3">
                  <RefreshCw className="h-5 w-5 text-blue-600 animate-spin" />
                  <div>
                    <p className="text-sm font-medium text-blue-800">
                      Sincronizando com sistema financeiro...
                    </p>
                    <p className="text-xs text-blue-600">
                      Aguarde enquanto os títulos são importados
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Resultado da Importação */}
            {importResult && (
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <div className="flex items-center space-x-3 mb-3">
                  {importResult.success ? (
                    <CheckCircle className="h-5 w-5 text-green-600" />
                  ) : (
                    <XCircle className="h-5 w-5 text-red-600" />
                  )}
                  <div>
                    <p className="text-sm font-medium text-gray-800">
                      {importResult.success ? 'Sincronização Concluída' : 'Erro na Sincronização'}
                    </p>
                    <p className="text-xs text-gray-600">
                      Período: {importResult.periodo.inicio} a {importResult.periodo.fim}
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-600">Recebidos:</span>
                    <span className="ml-2 font-medium">{importResult.totalReceived}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">Processados:</span>
                    <span className="ml-2 font-medium">{importResult.totalProcessed}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">Criados:</span>
                    <span className="ml-2 font-medium text-green-600">{importResult.totalCreated}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">Atualizados:</span>
                    <span className="ml-2 font-medium text-blue-600">{importResult.totalUpdated}</span>
                  </div>
                </div>

                {importResult.errors.length > 0 && (
                  <div className="mt-3">
                    <div className="flex items-center space-x-2 mb-2">
                      <AlertCircle className="h-4 w-4 text-red-500" />
                      <span className="text-sm font-medium text-red-700">
                        Erros ({importResult.errors.length})
                      </span>
                    </div>
                    <div className="max-h-32 overflow-y-auto">
                      {importResult.errors.map((error, index) => (
                        <p key={index} className="text-xs text-red-600 mb-1">
                          {error}
                        </p>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Botão de Importação */}
            <Button 
              onClick={handleImport} 
              disabled={importing || !period.data_inicio || !period.data_fim}
              className="w-full"
            >
              {importing ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  Sincronizando...
                </>
              ) : (
                <>
                  <Database className="h-4 w-4 mr-2" />
                  Sincronizar Títulos
                </>
              )}
            </Button>
          </>
        )}

        {loading && (
          <div className="flex items-center justify-center py-4">
            <RefreshCw className="h-5 w-5 animate-spin text-gray-500" />
            <span className="ml-2 text-sm text-gray-500">Carregando configuração...</span>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
